<template>
  <div
    class="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm transition-all duration-300"
    role="dialog"
    aria-modal="true"
  >
    <div
      class="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all p-6 sm:p-8 relative border border-gray-100 animate__animated animate__fadeInUp animate__faster"
    >
      <!-- Bouton fermer en haut à droite -->
      <button
        type="button"
        @click="onCancel"
        :disabled="loading"
        class="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-1.5 rounded-full hover:bg-gray-100 transition-colors disabled:opacity-50"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <!-- En-tête -->
      <div class="text-center mb-6">
        <div class="w-14 h-14 mx-auto mb-3 bg-primary-50 rounded-2xl flex items-center justify-center text-primary-600 shadow-inner">
          <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h3 class="text-2xl font-bold text-gray-900">
          {{ isSetup ? 'Configuration 2FA' : 'Vérification 2FA' }}
        </h3>
        <p class="text-sm text-gray-500 mt-1">
          {{
            isSetup
              ? 'Scannez le QR Code avec Google Authenticator'
              : 'Entrez le code généré par votre application'
          }}
        </p>
      </div>

      <!-- Erreur éventuelle -->
      <div
        v-if="errorMessage"
        class="mb-5 p-3.5 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-start gap-2.5"
      >
        <svg class="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>{{ errorMessage }}</span>
      </div>

      <!-- Formulaire d'activation (Setup) -->
      <div v-if="isSetup" class="space-y-4">
        <!-- QR Code -->
        <div v-if="qrUrl" class="flex flex-col items-center justify-center p-3 bg-gray-50 rounded-2xl border border-gray-100">
          <img
            :src="qrUrl"
            alt="QR Code 2FA"
            class="w-44 h-44 object-contain rounded-lg shadow-sm bg-white p-2"
          />
        </div>

        <!-- Clé secrète manuelle -->
        <div v-if="secretKey" class="text-center">
          <span class="text-xs text-gray-500 block mb-1 font-medium">Ou saisie manuelle de la clé :</span>
          <div class="flex items-center justify-center gap-2 bg-gray-100 px-3 py-2 rounded-xl border border-gray-200">
            <span class="font-mono text-sm tracking-wider font-semibold text-gray-800 select-all break-all">
              {{ secretKey }}
            </span>
            <button
              type="button"
              @click="copySecret"
              class="p-1 text-gray-500 hover:text-primary-600 transition-colors"
              :title="copied ? 'Copié !' : 'Copier la clé'"
            >
              <svg v-if="!copied" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <svg v-else class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Saisie du code (Commun Setup & Verify) -->
      <form @submit.prevent="onSubmit" class="mt-5 space-y-4">
        <div>
          <label for="twoFactorOtpInput" class="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2 text-center">
            Code de sécurité TOTP
          </label>
          <input
            id="twoFactorOtpInput"
            ref="otpInputRef"
            v-model="otpCode"
            type="text"
            inputmode="numeric"
            placeholder="Ex: 123456"
            class="w-full text-center text-2xl tracking-[0.3em] font-mono py-3 px-4 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-4 focus:ring-primary-100 outline-none transition-all"
            autofocus
            required
            :disabled="loading"
          />
        </div>

        <!-- Boutons d'action -->
        <div class="flex flex-col gap-2 pt-2">
          <button
            type="submit"
            :disabled="loading || !otpCode.trim()"
            class="buttonComponent w-full py-3 px-4 text-white font-semibold rounded-xl transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <svg
              v-if="loading"
              class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
            </svg>
            <span>{{ isSetup ? 'Confirmer et activer' : 'Valider la connexion' }}</span>
          </button>

          <button
            type="button"
            @click="onCancel"
            :disabled="loading"
            class="w-full py-2.5 px-4 text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors disabled:opacity-50"
          >
            Annuler la connexion
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'

const props = defineProps({
  isSetup: {
    type: Boolean,
    default: false,
  },
  qrUrl: {
    type: String,
    default: '',
  },
  secretKey: {
    type: String,
    default: '',
  },
  onVerify: {
    type: Function,
    required: true,
  },
  onSuccess: {
    type: Function,
    default: () => {},
  },
  onCancel: {
    type: Function,
    default: () => {},
  },
})

const otpCode = ref('')
const loading = ref(false)
const errorMessage = ref('')
const copied = ref(false)
const otpInputRef = ref(null)

onMounted(() => {
  setTimeout(() => {
    otpInputRef.value?.focus()
  }, 100)
})

async function copySecret() {
  if (!props.secretKey) return
  try {
    await navigator.clipboard.writeText(props.secretKey)
    copied.value = true
    setTimeout(() => {
      copied.value = false
    }, 2000)
  } catch (err) {
    console.warn('Erreur lors de la copie de la clé:', err)
  }
}

async function onSubmit() {
  const secretotp = otpCode.value.trim()
  if (!secretotp || loading.value) return

  loading.value = true
  errorMessage.value = ''

  try {
    const result = await props.onVerify(secretotp)
    if (result && result.success) {
      props.onSuccess(result)
    } else {
      errorMessage.value = result?.message || 'Code 2FA incorrect. Veuillez réessayer.'
      otpCode.value = ''
      otpInputRef.value?.focus()
    }
  } catch (error) {
    errorMessage.value = error.message || 'Une erreur est survenue lors de la validation'
  } finally {
    loading.value = false
  }
}
</script>
